import type {
  LeadListParams,
  LeadListResponse,
  LeadDetail,
  AnalyticsDashboard,
  LeadActionType,
  LeadAction,
  ApiResponse,
} from '@nordcrest/shared';
import { useAuthStore } from '../stores/authStore';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<ApiResponse<T>> {
  const token = useAuthStore.getState().token;

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  if (response.status === 401) {
    useAuthStore.getState().logout();
    throw new Error('Unauthorized');
  }

  const data = await response.json();
  return data as ApiResponse<T>;
}

// Auth
export async function login(username: string, password: string): Promise<{ token: string; expiresAt: string }> {
  const response = await fetch(`${API_BASE}/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  const data = await response.json();

  if (!data.success || !data.data) {
    throw new Error(data.error?.message || 'Login failed');
  }

  return data.data;
}

// Leads
export async function getLeads(params: LeadListParams = {}): Promise<LeadListResponse> {
  const searchParams = new URLSearchParams();

  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.temperature) searchParams.set('temperature', params.temperature);
  if (params.search) searchParams.set('search', params.search);
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);

  const response = await fetchApi<LeadListResponse>(`/admin/leads?${searchParams}`);

  if (!response.success || !response.data) {
    throw new Error(response.error?.message || 'Failed to fetch leads');
  }

  return response.data;
}

export async function getLeadDetail(id: string): Promise<LeadDetail> {
  const response = await fetchApi<LeadDetail>(`/admin/leads/${id}`);

  if (!response.success || !response.data) {
    throw new Error(response.error?.message || 'Failed to fetch lead');
  }

  return response.data;
}

export async function logLeadAction(
  leadId: string,
  actionType: LeadActionType,
  notes?: string
): Promise<LeadAction> {
  const response = await fetchApi<LeadAction>(`/admin/leads/${leadId}/actions`, {
    method: 'POST',
    body: JSON.stringify({ actionType, notes }),
  });

  if (!response.success || !response.data) {
    throw new Error(response.error?.message || 'Failed to log action');
  }

  return response.data;
}

// Analytics
export async function getAnalytics(): Promise<AnalyticsDashboard> {
  const response = await fetchApi<AnalyticsDashboard>('/admin/analytics');

  if (!response.success || !response.data) {
    throw new Error(response.error?.message || 'Failed to fetch analytics');
  }

  return response.data;
}

// Export
export function getExportUrl(): string {
  return `${API_BASE}/admin/leads/export/csv`;
}
